import moment from 'moment'
import React, { useMemo, useState } from 'react'
import SVG from 'react-inlinesvg'
import { postRequest } from '../../../services'
import {
  useApiPost,
  useSortableTable,
  TableHead,
  map,
  newTableSort,
  navigateTo,
  Header,
  Loadable,
  useDatePickerState,
  useApiGet,
  DatePicker,
  toArray,
  isBlank,
  calculateConversionValues,
  downloadCsv,
  filterBySearch,
  sleep,
  TableInfo,
  varClass,
} from '../../shared'
import { tableLabels } from '../statistics/constants'
import { Placeholder } from './Placeholder'

const sortClosed = newTableSort((a: any, b: any, key) => {
  if (key === 'trackerChannelUuids') {
    return [a.trackerChannelUuids.length, b.trackerChannelUuids.length]
  }
})

export const InsightClosed = (props) => {
  const datePickerState = useDatePickerState()

  console.log({
    fromDate: datePickerState.range.startDate,
    toDate: datePickerState.range.endDate,
  })
  const data = useApiPost<any[]>(
    '/insights/v1/closed',
    {
      fromDate: datePickerState.range.startDate,
      toDate: datePickerState.range.endDate,
      filters: {
        minClickCount: 50,
      },
    },
    [datePickerState.range.startDate, datePickerState.range.endDate]
  )
  const channels = useApiGet('/channels/v1/list')
  const networks = useApiGet('/networks/v1/networks')
  const advertisers = useApiGet('/advs/v1/minlist')

  return (
    <>
      <Header label="Closed campaigns" />
      <Loadable loaded={data} placeholder={<Placeholder />}>
        <Table
          {...{
            datePickerState,
            data,
            channels,
            networks,
            advertisers,
          }}
        />
      </Loadable>
    </>
  )
}

export const Table = (props) => {
  const { datePickerState, data, channels, networks, advertisers } = props

  const [csvDownloading, setCsvDownloading] = useState(false)

  const { sort, dir, toggleSort } = useSortableTable({
    sort: 'clickCount',
    dir: 'desc',
  })

  const shownRows = sortClosed(data, sort, dir)

  const headProps = { sort, dir, toggleSort }
  const itemProps = { channels, networks, advertisers }
  const isEmpty = data && isBlank(data)

  return (
    <>
      <DatePicker className="in-header" />

      <div className="card overview-performance m-t-20">
        <div className="card-body card-body-fill">
          <TableInfo>
            <div />
            <button
              className={varClass({
                'link action m-l-15': true,
                'loader-btn': csvDownloading,
              })}
              disabled={csvDownloading}
              onClick={async () => {
                setCsvDownloading(true)

                const columns = {
                  'trackerProgramId': 'Campaign',
                  'trackerAdvertiserUuid': 'Merchant',
                  'trackerChannelUuids': 'Sites',
                  'trackerNetworkUuid': 'Network',
                  'clickCount': 'Clicks',
                }
                try {
                  downloadCsv({
                    filename: `closed_campaigns_${moment(
                      datePickerState.range.startDate
                    ).format('YYYY-MM-DD')}-${moment(
                      datePickerState.range.endDate
                    ).format('YYYY-MM-DD')}.csv`,
                    columns: Object.keys(columns) as any,
                    rows: shownRows,
                    columnFormatter: (column) => columns[column] || '',
                    rowFormatter: (row) => {
                      const trackerAdvertiser = advertisers?.find(
                        (advertiser) =>
                          advertiser.advertiserUuid ===
                          row.trackerAdvertiserUuid
                      )
                      const trackerChannels = channels?.filter((channel) =>
                        row.trackerChannelUuids.includes(channel.channelUuid)
                      )
                      const trackerNetwork = networks?.find(
                        (network) =>
                          network.networkUuid === row.trackerNetworkUuid
                      )
                      return {
                        'trackerProgramId': row.trackerProgramId.replace(
                          /^\w+-/,
                          ''
                        ),
                        'trackerAdvertiserUuid':
                          trackerAdvertiser?.advertiserDomain || '',
                        'trackerChannelUuids':
                          trackerChannels
                            ?.map((channel) => channel.channelName)
                            ?.join(', ') || '',
                        'trackerNetworkUuid': trackerNetwork?.networkName || '',
                        'clickCount': row.clickCount,
                      }
                    },
                  })
                } catch (error) {
                  console.error(error)
                } finally {
                  setCsvDownloading(false)
                }
              }}
            >
              <SVG src="/images/insights/icon-csv.svg" className="m-r-10" />
              Download CSV
            </button>
          </TableInfo>

          <div className="table-container-overflow">
            <table className="table table-bordered table-sortable table-hoverable">
              <thead>
                <tr>
                  <TableHead
                    value="trackerProgramId"
                    label="Campaign"
                    {...headProps}
                  />
                  <TableHead
                    value="trackerNetworkUuid"
                    label="Network"
                    {...headProps}
                  />
                  <TableHead
                    value="trackerChannelUuids"
                    label="Sites"
                    {...headProps}
                  />
                  <TableHead
                    value="clickCount"
                    label="Clicks"
                    {...headProps}
                    desc
                  />
                </tr>
              </thead>
              <tbody>
                {map(shownRows, (item) => (
                  <Item
                    key={item.trackerProgramId}
                    item={item}
                    {...itemProps}
                  />
                ))}
                {isEmpty && (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-center text-light text-bolder"
                    >
                      Nothing found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}

const Item = (props) => {
  const { item, channels, networks, advertisers } = props

  const programId = item.trackerProgramId.replace(/^\w+-/, '')

  const [trackerAdvertiser, trackerChannels, trackerNetwork] = useMemo(() => {
    const trackerAdvertiser = advertisers?.find(
      (advertiser) => advertiser.advertiserUuid === item.trackerAdvertiserUuid
    )
    const trackerChannels = channels?.filter((channel) =>
      item.trackerChannelUuids.includes(channel.channelUuid)
    )
    const trackerNetwork = networks?.find(
      (network) => network.networkUuid === item.trackerNetworkUuid
    )
    return [trackerAdvertiser, trackerChannels, trackerNetwork]
  }, [item, channels, networks, advertisers])

  return (
    <tr>
      <td>
        <div className="row row-center row-space-between row-nowrap">
          <div className="flex-1 text-nowrap">
            {trackerAdvertiser ? (
              <>
                <div className="text-dark text-bold m-b-1">
                  {trackerAdvertiser.advertiserDomain}
                </div>
                <div className="text-light">{programId}</div>
              </>
            ) : (
              <div className="text-dark text-bold">{programId}</div>
            )}
          </div>

          <button
            className="link link-new-lighter link-filter"
            onClick={async (event) => {
              navigateTo(event, `/merchants/${item.trackerAdvertiserUuid}`)
            }}
          >
            <SVG src="/images/icon-details.svg" className="vertical-middle" />
          </button>
        </div>
      </td>
      <td>
        {trackerNetwork && (
          <div
            key={trackerNetwork.networkUuid}
            className="badge text-smaller badge-hoverable text-bold text-nowrap"
            onClick={(event) => {
              navigateTo(event, `/networks/${trackerNetwork.networkUuid}`)
            }}
          >
            {trackerNetwork.networkName}
          </div>
        )}
      </td>
      <td>
        {map(trackerChannels, (channel) => (
          <div
            key={channel.channelUuid}
            className="badge text-smaller badge-hoverable text-bold text-nowrap m-1"
          >
            {channel.channelName}
          </div>
        ))}
      </td>
      <td>
        <div className="top-value top-value-orange text-nowrap">
          {item.clickCount}
        </div>
      </td>
    </tr>
  )
}
